import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/** Converte string ISO do backend (UTC sem Z) para Date corretamente. */
export function parseUTC(date: string | Date): Date {
  if (date instanceof Date) return date;
  // Backend retorna UTC sem sufixo Z — JS interpreta como local sem ele
  if (!date.endsWith('Z') && !date.includes('+')) return new Date(`${date}Z`);
  return new Date(date);
}

export function formatDate(date: string | Date): string {
  return format(parseUTC(date), 'dd/MM/yyyy');
}

export function formatTime(date: string | Date): string {
  return format(parseUTC(date), 'HH:mm');
}

export function formatRelativeTime(date: string | Date): string {
  const d = parseUTC(date);
  if (isToday(d)) return formatTime(d);
  if (isYesterday(d)) return 'Ontem';
  return formatDistanceToNow(d, { addSuffix: true, locale: ptBR });
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  // +55 (DD) 9XXXX-XXXX (13 dígitos — celular com DDI)
  if (cleaned.length === 13 && cleaned.startsWith('55')) {
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }
  // +55 (DD) XXXX-XXXX (12 dígitos — fixo com DDI ou celular sem nono dígito)
  if (cleaned.length === 12 && cleaned.startsWith('55')) {
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
  }
  // (DD) 9XXXX-XXXX (11 dígitos — celular sem DDI)
  if (cleaned.length === 11) {
    return `+55 (${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // (DD) XXXX-XXXX (10 dígitos — fixo sem DDI)
  if (cleaned.length === 10) {
    return `+55 (${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

export function formatCNPJ(cnpj: string): string {
  const c = cnpj.replace(/\D/g, '');
  return c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function formatCPF(cpf: string): string {
  const c = cpf.replace(/\D/g, '');
  return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function truncateText(text: string, maxLength: number = 50): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

interface ConnectionLabelInput {
  wa_number_label?: string;
}

/**
 * Nome a exibir como "via {nome}" da conexão WhatsApp no chamado.
 *
 * Usa apenas o rótulo da conexão real (`wa_number_label`, cadastrado em
 * /whatsapp/conexao). Sem conexão real definida, retorna string vazia — nesse
 * caso o "via {nome}" não deve ser renderizado (sem fallback para departamento
 * nem nome genérico).
 */
export function getConnectionLabel(conversation: ConnectionLabelInput): string {
  return conversation.wa_number_label || '';
}
